import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { GS1NotificationService } from '@gs1france/gs1-components-library';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { GS1Document } from '../models/document.model';
import { LogoPartenairesVM } from '../models/logo.model';

@Injectable({ providedIn: 'root' })
export class DocumentService {
  __URL_API__ = `${environment.URL_API}`;

  private _http = inject(HttpClient);
  private notificationService = inject(GS1NotificationService);
  private translate = inject(TranslateService);

  searchFactures(skip = 0, limit = 10): Observable<{ count: number; data: GS1Document[] }> {
    const params = new HttpParams().set('skip', String(skip)).set('limit', String(limit));

    return this._http
      .get<{
        count: number;
        data: GS1Document[];
      }>(this.__URL_API__ + 'api/Document/GetFactures', { params })
      .pipe(
        catchError((error) => {
          console.error(error);
          return of(null);
        })
      );
  }

  getCGA(): Observable<GS1Document[]> {
    return this._http.get<GS1Document[]>(this.__URL_API__ + 'api/Document/GetCGA').pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  getCGU(): Observable<GS1Document> {
    return this._http.get<GS1Document>(this.__URL_API__ + 'api/Document/GetCGU').pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  getCGV(): Observable<GS1Document> {
    return this._http.get<GS1Document>(this.__URL_API__ + 'api/Document/GetCGV').pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  getPolitiqueConfidentialite(): Observable<GS1Document> {
    return this._http.get<GS1Document>(this.__URL_API__ + 'api/Document/GetPolitiqueConfidentialite').pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  getPackDocuments(): Observable<GS1Document[]> {
    return this._http.get<GS1Document[]>(this.__URL_API__ + 'api/Document/GetPackDocuments').pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  searchMesDocuments(skip = 0, limit = 10): Observable<{ count: number; data: GS1Document[] }> {
    const params = new HttpParams().set('skip', skip).set('limit', String(limit));

    return this._http
      .get<{
        count: number;
        data: GS1Document[];
      }>(this.__URL_API__ + 'api/Document/GetMesDocuments', { params })
      .pipe(
        catchError((error) => {
          this.notificationService.addNotification(this.translate.instant('MesDocuments.PROBLEME_RECUPERATION_DOCUMENTS'), 'error');
          console.error(error);
          return of(null);
        })
      );
  }

  getLogosPartenaires(): Observable<LogoPartenairesVM> {
    return this._http.get<LogoPartenairesVM>(this.__URL_API__ + 'api/Document/GetLogosPartenaires').pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  downloadDocument(idDoc: string): Observable<any> {
    return this._http.get(this.__URL_API__ + 'api/Document/DownloadFile/' + idDoc, { responseType: 'blob', observe: 'response' }).pipe(
      take(1),
      tap((response) => {
        const blob = response.body;
        if (blob !== null) {
          const contentType = response.headers.get('Content-Type');
          const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
          if (contentType.includes('application/pdf')) {
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener';
            a.download = idDoc + '.pdf';
            a.click();
          }
          if (contentType.includes('text/html')) window.open(url, '_blank', 'noopener');
        }
      }),
      catchError((err) => {
        this.notificationService.addNotification(this.translate.instant('invoices.download-error'), 'error');
        console.error(err);
        return of(null);
      })
    );
  }

  DownloadCGA(): Observable<Blob> {
    const fileName = "Conditions Générales d'Adhésion - GS1 France";
    return this._http
      .get(this.__URL_API__ + 'api/Document/GetPdfCGA', {
        responseType: 'blob' as const
      })
      .pipe(
        tap((data) => {
          const file = new Blob([data as any], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
          const a = document.createElement('a');
          a.href = fileURL;
          a.target = '_blank';
          a.rel = 'noopener';
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
        }),
        catchError((error) => {
          console.error(error);
          return of(null);
        })
      );
  }

  DisplayDocument(idDoc: string): Observable<string> {
    return this._http
      .get(this.__URL_API__ + 'api/Document/DisplayFile/' + idDoc, {
        responseType: 'blob'
      })
      .pipe(
        map((blob) => window.URL.createObjectURL(blob)),
        catchError((error) => {
          console.error(error);
          return of(null);
        })
      );
  }

  GetMembershipReport(): Observable<GS1Document> {
    return this._http.get<GS1Document>(this.__URL_API__ + 'api/Document/GetMembershipReport').pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  private generateOneTimeAccessToken(idDoc: string): Observable<{ oneTimeToken: string }> {
    return this._http
      .get<{
        oneTimeToken: string;
      }>(this.__URL_API__ + 'api/Document/GenerateOneTimeAccessToken/' + idDoc)
      .pipe(
        catchError(() => {
          console.error('Error generating one-time access token');
          return of({ oneTimeToken: '' });
        })
      );
  }
}
